/** Player settings, persisted locally. Null means "follow the system". */
import type { Locale, ThemeId } from "@mistsweeper/assets";
import type { Mode } from "@mistsweeper/core";
import { isLocale } from "./i18n.ts";
import { getJson, setJson } from "./storage.ts";

export interface Settings {
  readonly v: 1;
  mode: Mode;
  theme: ThemeId | null;
  locale: Locale | null;
  /** Null follows the system's reduced motion setting. */
  reduceMotion: boolean | null;
  /** Device pixels per art unit; null picks a default from the screen. */
  scale: number | null;
  bannerMode: boolean;
  /** The music (decision 0022), on until the player turns it off. */
  music: boolean;
  /** The effects and the ambience (decision 0022), on until the player turns them off. */
  sounds: boolean;
  /** The start hint has shown and the player has revealed a tile: it does not show again. */
  hintSeen: boolean;
  debug: boolean;
  /**
   * The tilt-shift's strength from the developer setting, from 0, off, to 1; null follows the
   * default, TILT_DEFAULT. Kept under a name of its own since it became the default: the trial
   * saved 0 for everyone who saved a setting.
   */
  tiltShift: number | null;
  /**
   * The player's own cabin (decision 0029): a found house's model, standing in the cabin's place
   * on this screen in every expedition and clearing; null for the first cabin.
   */
  home: string | null;
  /** The clearing has said, once, that the cabin can be any house the player has found. */
  homeHintSeen: boolean;
  /** The lesson (decision 0045) was played or skipped: it does not open by itself again. */
  lessonSeen: boolean;
}

/** The tilt-shift's strength unless a developer has set another (Daniel, 2026-10-20). */
export const TILT_DEFAULT = 0.7;

const DEFAULTS: Settings = {
  v: 1,
  mode: "daily",
  theme: null,
  locale: null,
  reduceMotion: null,
  scale: null,
  bannerMode: false,
  music: true,
  sounds: true,
  hintSeen: false,
  debug: false,
  tiltShift: null,
  home: null,
  homeHintSeen: false,
  lessonSeen: false,
};

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

export function loadSettings(): Settings {
  const saved = getJson<Partial<Settings> & { scale3d?: unknown }>("settings");
  if (saved?.v !== 1) return { ...DEFAULTS };
  return {
    ...DEFAULTS,
    mode: saved.mode === "endless" ? "endless" : "daily",
    theme: saved.theme === "day" || saved.theme === "night" ? saved.theme : null,
    locale: isLocale(saved.locale) ? saved.locale : null,
    reduceMotion: typeof saved.reduceMotion === "boolean" ? saved.reduceMotion : null,
    // The 3D world's zoom was kept apart while the flat world lived on (decision 0009, phase A).
    scale: num(saved.scale3d) ?? num(saved.scale),
    bannerMode: saved.bannerMode === true,
    music: saved.music !== false,
    sounds: saved.sounds !== false,
    hintSeen: saved.hintSeen === true,
    debug: saved.debug === true,
    tiltShift: (() => {
      const v = num(saved.tiltShift);
      return v === null ? null : Math.min(1, Math.max(0, v));
    })(),
    home:
      typeof saved.home === "string" && /^iso-[a-z]+-[a-z0-9]+$/.test(saved.home)
        ? saved.home
        : null,
    homeHintSeen: saved.homeHintSeen === true,
    lessonSeen: saved.lessonSeen === true,
  };
}

export function saveSettings(settings: Settings): void {
  setJson("settings", settings);
}

/**
 * The reference zoom, about ten tiles across a phone at 0.8 points per art unit: the isometric
 * view of the earlier flat renderer. The zoom limits are set from it.
 */
export function referenceScale(devicePixelRatio: number): number {
  if (devicePixelRatio >= 3) return 2.4;
  if (devicePixelRatio >= 2) return 1.6;
  return 1.0;
}

/**
 * A run starts this much closer than the reference, so a tile is about 35 points high and the
 * numbers show whole from the start (decision 0010): about five and a half tiles across a phone.
 */
export const START_ZOOM = 1.85;

export function defaultScale(devicePixelRatio: number): number {
  return referenceScale(devicePixelRatio) * START_ZOOM;
}
